import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SITE_URL = 'https://gvnemt.com';
const SITE_TITLE = 'Guardian Veterans – Veteran-Owned NEMT Services in West Valley AZ';
const SHARE_TEXT = 'Know someone who needs reliable medical transportation? Guardian Veterans LLC provides compassionate, veteran-owned NEMT services in the West Valley AZ area. Check them out!';

export default function Footer() {
  const navigate = useNavigate();
  const [emailForm, setEmailForm] = useState({ name: '', email: '', recipientEmail: '', message: '' });
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copyToast, setCopyToast] = useState('');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}`,
    nextdoor: `https://nextdoor.com/share/?body=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SITE_URL)}`,
  };

  const copyLink = async (message: string) => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopyToast(message);
      setTimeout(() => setCopyToast(''), 3500);
    } catch {
      setCopyToast('Could not copy — please copy manually: ' + SITE_URL);
      setTimeout(() => setCopyToast(''), 5000);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailForm.recipientEmail || !emailForm.message) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: emailForm.name,
        email: emailForm.email,
        recipientEmail: emailForm.recipientEmail,
        message: emailForm.message,
      });
      await fetch('https://readdy.ai/api/form/d6p3aof5ne2i7jjrvva0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setEmailSent(true);
      setEmailForm({ name: '', email: '', recipientEmail: '', message: '' });
      setTimeout(() => {
        setEmailSent(false);
        setShowEmailModal(false);
      }, 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-16 border-t border-yellow-500/20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-4">
              <a href="/" className="cursor-pointer inline-block">
                <img
                  src="https://static.readdy.ai/image/870e0649015e5e428345464cd8040611/e2821367afaf48576bf0fa4cd75aa99b.png"
                  alt="Guardian Veterans LLC"
                  className="h-[58px] w-auto object-contain"
                />
              </a>
            </div>
            <p className="text-yellow-400 font-semibold mb-2">Veteran Owned and Operated</p>
            <p className="text-gray-400 leading-relaxed">
              Non-Emergency Medical Transportation (NEMT) services for the West Valley Arizona community. Veteran-owned &amp; operated.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Quick Links</h4>
            <div className="space-y-3">
              <button onClick={() => navigate('/services')} className="block text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-left whitespace-nowrap">
                Services
              </button>
              <button onClick={() => scrollToSection('facility-form')} className="block text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-left whitespace-nowrap">
                For Facilities
              </button>
              <button onClick={() => scrollToSection('private-pay-form')} className="block text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-left whitespace-nowrap">
                For Patients
              </button>
              <button onClick={() => scrollToSection('service-area')} className="block text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-left whitespace-nowrap">
                Service Area
              </button>
              <button onClick={() => scrollToSection('contact-us')} className="block text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-left whitespace-nowrap">
                Contact Us
              </button>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Contact Information</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-yellow-400 text-lg"></i>
                <a href="tel:+16022956626" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  (602) 295-6626
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-mail-line text-yellow-400 text-lg"></i>
                <a href="mailto:info@gvnemt.com" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  info@gvnemt.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-yellow-400 text-lg mt-0.5"></i>
                <div>
                  <span className="text-gray-300 block">Waddell, AZ 85355</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Service Areas</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
              <span>Sun City</span>
              <span>Peoria</span>
              <span>Sun City West</span>
              <span>Surprise</span>
              <span>Goodyear</span>
              <span>Glendale</span>
              <span>El Mirage</span>
              <span>Scottsdale</span>
            </div>
          </div>
        </div>

        {/* Spread the Word */}
        <div className="border-t border-yellow-500/20 pt-10 mb-10">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <i className="ri-share-line text-yellow-400 text-2xl"></i>
              <h4 className="text-xl font-bold text-white">Spread the Word</h4>
            </div>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Know a loved one, neighbor, or facility that could benefit from our services?<br />
              <span className="text-yellow-400 font-semibold italic">The more you share, the more we can help — thank you!</span>
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {/* Facebook */}
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 bg-[#1877F2] hover:bg-[#1565d8] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
            >
              <i className="ri-facebook-fill text-lg"></i>
              Share on Facebook
            </a>

            {/* Instagram — copy link + open Instagram */}
            <button
              onClick={async () => {
                await copyLink('Link copied! Instagram is opening — just paste it in your post or bio.');
                window.open('https://www.instagram.com', '_blank', 'noopener,noreferrer');
              }}
              className="flex items-center gap-2 bg-gradient-to-r from-[#f09433] via-[#e6683c] to-[#bc1888] hover:opacity-90 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
              title="Copy link and open Instagram"
            >
              <i className="ri-instagram-line text-lg"></i>
              Share on Instagram
            </button>

            {/* Nextdoor */}
            <a
              href={shareLinks.nextdoor}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 bg-[#00B246] hover:bg-[#009a3c] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
            >
              <i className="ri-community-line text-lg"></i>
              Share on Nextdoor
            </a>

            {/* Email */}
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
            >
              <i className="ri-mail-send-line text-lg"></i>
              Share via Email
            </button>
          </div>
        </div>

        <div className="border-t border-yellow-500/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © 2026 Guardian Veterans LLC - All Rights Reserved
            </p>
            <div className="flex items-center gap-4 text-sm">
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-slate-400 hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Privacy Policy
              </button>
              <span className="text-slate-600">·</span>
              <button
                onClick={() => navigate('/terms-of-service')}
                className="text-slate-400 hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Terms of Service
              </button>
            </div>
            <p className="text-yellow-400 font-semibold italic">
              "We stand for those who stood for us."
            </p>
          </div>
        </div>
      </div>

      {/* Copy Toast */}
      {copyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 border border-yellow-500/40 text-white text-sm font-medium px-5 py-3 rounded-lg flex items-center gap-2 animate-fade-in">
          <i className="ri-checkbox-circle-line text-yellow-400 text-lg"></i>
          {copyToast}
        </div>
      )}

      {/* Email Share Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-slate-800 border border-yellow-500/30 rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => { setShowEmailModal(false); setEmailSent(false); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-mail-send-line text-yellow-400 text-xl"></i>
              <h3 className="text-lg font-bold text-white">Share via Email</h3>
            </div>
            <p className="text-gray-400 text-sm mb-5">Send our website to a loved one or facility that may need our services.</p>

            {emailSent ? (
              <div className="text-center py-6">
                <i className="ri-checkbox-circle-line text-yellow-400 text-4xl mb-3 block"></i>
                <p className="text-white font-semibold">Thank you for spreading the word!</p>
                <p className="text-gray-400 text-sm mt-1">Your message has been sent.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={emailForm.name}
                    onChange={e => setEmailForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="John Smith"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Your Email</label>
                  <input
                    type="email"
                    name="email"
                    value={emailForm.email}
                    onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Recipient's Email <span className="text-yellow-400">*</span></label>
                  <input
                    type="email"
                    name="recipientEmail"
                    required
                    value={emailForm.recipientEmail}
                    onChange={e => setEmailForm(f => ({ ...f, recipientEmail: e.target.value }))}
                    placeholder="friend@example.com"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Personal Message <span className="text-yellow-400">*</span></label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    value={emailForm.message}
                    onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={`Hey! I thought you might benefit from Guardian Veterans LLC — a veteran-owned NEMT service in the West Valley AZ area. Check them out at ${SITE_URL}`}
                    rows={4}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none"
                  />
                  <p className="text-xs text-gray-500 text-right mt-1">{emailForm.message.length}/500</p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 disabled:opacity-60 text-black font-bold py-2.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </footer>
  );
}
